/**
 * useFilterConfig — Filter configuration hook for BPA Findings.
 *
 * Follows the same pattern as IntegrationListing/Filters.config.tsx:
 *  - Reads initial filter values from URL query params on mount.
 *  - Fetches dynamic filter options (providers, modules, regions, benchmarks)
 *    from the API — no hardcoded lists.
 *  - Uses `FilterConfig` from SearchAndFilters to declare each filter.
 *  - Integrates with Jotai atoms so the grid reacts automatically.
 *  - The FilterConfig object is memoised to avoid recreation on every render.
 */
import { AwsLogo, AzureLogo, GCPLogo, M365Logo } from 'assets';
import {
  DateRangeFilterConfig,
  FilterConfig,
  FilterGroupConfig,
  InputFilterConfig,
  SearchInputFilterConfig,
} from 'components/SearchAndFilters/FilterTypes';
import { IconMultiSelectFilterConfig } from 'components/SearchAndFilters/IconMultiSelectFilterConfig';
import {
  MultiSelectFilterConfig,
  MultiSelectRenderStyle,
} from 'components/SearchAndFilters/MultiSelect';
import history from 'history.js';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { stableStringify } from 'lib/helper';
import { showFailureToast, showInfoToast } from 'lib/ToastHelper';
import React, { useEffect, useRef, useState } from 'react';
import { replaceBrowserSearch } from 'views/bpaUrlFilterUtils';
import { companyIdAtom } from 'views/state';

import { fetchBPAFindingsOptions } from './services/bpaFindingsService';
import {
  filterValuesAtom,
  findingsOptionsDataAtom,
  frameworkIdFilterAtom,
  isQuerystringFiltersInitializedAtom,
  parsedFilterValuesAtom,
} from './state';
import { parseBPAFindingsUrlFilters } from './urlFilters';

// ---------------------------------------------------------------------------
// API response normalisation
// ---------------------------------------------------------------------------

/**
 * The backend may return option objects with either a `title` field
 * (integration-options style) or a `label` field (direct style).
 * Normalise both shapes to `{ label, value }` which is what
 * `MultiSelectFilterConfig.options` (Option[]) requires.
 */
interface ApiOption {
  label?: string;
  title?: string;
  value: string;
}

/**
 * Extended shape for benchmark/integration API options which additionally carry
 * `provider` and `module` fields used for logo resolution and client-side
 * filtering when the provider/module badge filter is active.
 */
interface BenchmarkApiOption extends ApiOption {
  provider?: string;
  module?: string;
}

interface IntegrationApiOption extends ApiOption {
  provider?: string;
  module?: string;
}

/**
 * Maps a lowercase cloud-provider code to its logo image URL.
 * Add new entries here as additional providers are supported.
 */
const PROVIDER_IMAGE_MAP: Record<string, string> = {
  aws: AwsLogo as string,
  azure: AzureLogo as string,
  gcp: GCPLogo as string,
  m365: M365Logo as string,
};

const normalizeOption = (opt: ApiOption): { label: string; value: string } => ({
  label: opt.label ?? opt.title ?? opt.value,
  value: opt.value,
});

const getEmptyFindingsOptionsData = () => ({
  providers: [],
  modules: [],
  regions: [],
  benchmarks: [],
  integrations: [],
});

const getProvisionalIntegrationOptions = (): {
  label: string;
  value: string;
}[] => {
  const params = new URLSearchParams(window.location.search);
  const id = params.get('integration');
  const label = (
    history.location?.state as { integrationLabel?: string } | undefined
  )?.integrationLabel;

  return id && label ? [{ label, value: id }] : [];
};

/**
 * Normalises a benchmark API option, adding an `imageSrc` logo from
 * PROVIDER_IMAGE_MAP and preserving `provider` / `module` for client-side
 * filtering inside the useMemo.
 */
const normalizeBenchmarkOption = (
  opt: BenchmarkApiOption,
): {
  label: string;
  value: string;
  imageSrc?: string;
  provider?: string;
  module?: string;
} => ({
  label: opt.label ?? opt.title ?? opt.value,
  value: opt.value,
  ...(opt.provider && PROVIDER_IMAGE_MAP[opt.provider]
    ? { imageSrc: PROVIDER_IMAGE_MAP[opt.provider] }
    : {}),
  ...(opt.provider ? { provider: opt.provider } : {}),
  ...(opt.module ? { module: opt.module } : {}),
});

/**
 * Normalises an integration API option, adding an `imageSrc` logo from
 * PROVIDER_IMAGE_MAP and preserving `provider` / `module` for client-side
 * filtering.
 */
const normalizeIntegrationOption = (
  opt: IntegrationApiOption,
): {
  label: string;
  value: string;
  imageSrc?: string;
  provider?: string;
  module?: string;
} => {
  // The API may return the integration id as a number. Deep-link URLs and the
  // filter atoms always carry string values, so coerce to string here to keep
  // option lookups (badge label resolution, "selected" state) working via ===.
  const value = String(opt.value);
  return {
    label: opt.label ?? opt.title ?? value,
    value,
    ...(opt.provider && PROVIDER_IMAGE_MAP[opt.provider]
      ? { imageSrc: PROVIDER_IMAGE_MAP[opt.provider] }
      : {}),
    ...(opt.provider ? { provider: opt.provider } : {}),
    ...(opt.module ? { module: opt.module } : {}),
  };
};

/**
 * Normalises the regions API response (either flat or grouped by provider)
 * to a flat list of icon-aware options.  When the grouped format is detected,
 * the provider key is used to resolve an `imageSrc` logo so that each region
 * shows its cloud-provider icon in the IconMultiSelect dropdown.
 *
 * Grouped format (actual API shape):
 *   [{ "aws": [{ title, value }, …] }, …]
 * Flat format (fallback):
 *   [{ title, value }, …]
 */
const normalizeRegions = (
  raw: unknown,
): { label: string; value: string; imageSrc?: string; provider?: string }[] => {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const first = raw[0] as Record<string, unknown>;
  if (typeof first === 'object' && first !== null && !('value' in first)) {
    // Grouped format: extract provider key to resolve the logo and enable
    // client-side filtering by provider.
    return (raw as Record<string, ApiOption[]>[]).flatMap((group) =>
      Object.entries(group).flatMap(([provider, regions]) =>
        regions.map((opt) => ({
          label: opt.label ?? opt.title ?? opt.value,
          value: opt.value,
          ...(PROVIDER_IMAGE_MAP[provider]
            ? { imageSrc: PROVIDER_IMAGE_MAP[provider] }
            : {}),
          provider,
        })),
      ),
    );
  }
  return (raw as ApiOption[]).map(normalizeOption);
};

export const useFilterConfig = (): FilterConfig => {
  const setQuerystringFiltersInitialized = useSetAtom(
    isQuerystringFiltersInitializedAtom,
  );
  const isQuerystringFiltersInitialized = useAtomValue(
    isQuerystringFiltersInitializedAtom,
  );
  const setFilterValues = useSetAtom(filterValuesAtom);
  const rawFilterValues = useAtomValue(filterValuesAtom);
  const filterValues = useAtomValue(parsedFilterValuesAtom);
  const [, setFrameworkId] = useAtom(frameworkIdFilterAtom);
  const companyId = useAtomValue(companyIdAtom);
  const setFindingsOptionsData = useSetAtom(findingsOptionsDataAtom);

  // Holds the URL-derived filter values (stable-stringified) that must be
  // reflected in `filterValuesAtom` before we allow the grid/insights to fetch.
  // `null` means "nothing pending" (either no URL filters, or already applied).
  const [pendingHydration, setPendingHydration] = useState<string | null>(null);

  // Dynamic filter options populated from the API
  const [providerOptions, setProviderOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [moduleOptions, setModuleOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [regionOptions, setRegionOptions] = useState<
    { label: string; value: string; imageSrc?: string; provider?: string }[]
  >([]);
  const [benchmarkOptions, setBenchmarkOptions] = useState<
    {
      label: string;
      value: string;
      imageSrc?: string;
      provider?: string;
      module?: string;
    }[]
  >([]);
  const [integrationOptions, setIntegrationOptions] = useState<
    {
      label: string;
      value: string;
      imageSrc?: string;
      provider?: string;
      module?: string;
    }[]
  >(() => {
    // Seed a provisional integration option from the router history state that
    // the deep-link carries, so the Integration filter badge shows the
    // integration *name* from the first paint instead of briefly flashing the
    // raw id while the options API loads. The URL is kept clean (id only); the
    // label travels in history state. Once the real options arrive they replace
    // this list (same value → the transition is seamless). On a hard refresh
    // the state is absent, so the label resolves from the loaded options.
    return getProvisionalIntegrationOptions();
  });

  // ── Load dynamic filter options from the API ───────────────────────────────
  useEffect(() => {
    const clearOptions = () => {
      setProviderOptions([]);
      setModuleOptions([]);
      setRegionOptions([]);
      setBenchmarkOptions([]);
      setIntegrationOptions([]);
      setFindingsOptionsData(getEmptyFindingsOptionsData());
    };

    if (!companyId || companyId === -1) {
      clearOptions();
      return;
    }

    let cancelled = false;
    const provisionalIntegrations = getProvisionalIntegrationOptions();
    setProviderOptions([]);
    setModuleOptions([]);
    setRegionOptions([]);
    setBenchmarkOptions([]);
    setIntegrationOptions(provisionalIntegrations);
    setFindingsOptionsData({
      ...getEmptyFindingsOptionsData(),
      integrations: provisionalIntegrations,
    });

    fetchBPAFindingsOptions(
      companyId,
      'providers,modules,regions,benchmarks,integrations',
    )
      .then((res) => {
        if (cancelled) return;

        if (res.status === 200 && res.data) {
          const providers = ((res.data.providers as ApiOption[]) || []).map(
            normalizeOption,
          );
          const modules = ((res.data.modules as ApiOption[]) || []).map(
            normalizeOption,
          );
          const regions = normalizeRegions(res.data.regions);
          const benchmarks = (
            (res.data.benchmarks as BenchmarkApiOption[]) || []
          ).map(normalizeBenchmarkOption);
          const integrations = (
            (res.data.integrations as IntegrationApiOption[]) || []
          ).map(normalizeIntegrationOption);

          setProviderOptions(providers);
          setModuleOptions(modules);
          setRegionOptions(regions);
          setBenchmarkOptions(benchmarks);
          // Merge (not replace) so any provisional deep-link integration option
          // seeded from history state survives even if the API response does
          // not include that integration — the badge never reverts to the id.
          setIntegrationOptions((prev) => {
            const apiValues = new Set(integrations.map((o) => o.value));
            const provisionalExtras = prev.filter(
              (o) => !apiValues.has(o.value),
            );
            return [...provisionalExtras, ...integrations];
          });

          setFindingsOptionsData((prev) => ({
            ...prev,
            providers,
            modules,
            regions,
            benchmarks,
            integrations,
          }));
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;

        const message =
          err instanceof Error ? err.message : 'Failed to load filter options';
        showFailureToast(message);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, setFindingsOptionsData]);

  // ── Hydrate filters from URL query string on first render ──────────────────
  useEffect(() => {
    setQuerystringFiltersInitialized(false);

    // Valid URL filters win completely. With no valid URL filters, leave the
    // existing in-memory atoms untouched so SPA navigation preserves them.
    const hydration = parseBPAFindingsUrlFilters(window.location.search);
    // Apply the URL filters, but defer marking initialisation as complete
    // until `filterValuesAtom` actually reflects these values. Flipping the
    // guard synchronously here would let the grid/insights fetch once with
    // the DEFAULT filters and again once the atom update lands — a redundant
    // double round-trip on every deep-link entry.
    //
    // NOTE: We intentionally do NOT wait for the filter-options API here.
    // Initialisation (and therefore the grid/insights fetch) must not be
    // coupled to the options request. Selections that resolve against async
    // option lists (e.g. the Integration badge label) are hydrated later:
    // the config useMemo re-runs `applyInitialFilterValues` when the options
    // load, so the badge switches from the raw id to the integration name
    // without blocking data loading.
    if (hydration.hasValidUrlFilters) {
      setFilterValues(hydration.filterValues);
      setFrameworkId(hydration.routeFilter);
      setPendingHydration(stableStringify(hydration.filterValues));
    } else {
      // No atom update is pending, so the existing in-memory filters are ready.
      setQuerystringFiltersInitialized(true);
    }
    replaceBrowserSearch(hydration.canonicalSearch);
    return () => {
      setQuerystringFiltersInitialized(false);
    };
    // Deps are the stable Jotai setter references — no other reactive
    // values are read inside this effect, so the dependency array is complete.
  }, [setFilterValues, setFrameworkId, setQuerystringFiltersInitialized]);

  // ── Flip the init guard only once the atom reflects the URL filters ────────
  // Prevents the redundant "default filters" fetch described above.
  useEffect(() => {
    if (pendingHydration === null) return;
    if (stableStringify(rawFilterValues) === pendingHydration) {
      setPendingHydration(null);
      setQuerystringFiltersInitialized(true);
    }
  }, [pendingHydration, rawFilterValues, setQuerystringFiltersInitialized]);

  // ── Stable ref — lets the reset effect read current filterValues without
  //    adding filterValues to the effect's dep array (which would fire on every
  //    user-driven filter change, not just provider / module changes). ──────────
  const filterValuesRef = useRef(filterValues);
  filterValuesRef.current = filterValues;

  // ── Derive current provider / module selections ───────────────────────────────
  // Sorted, comma-joined strings are used as useMemo / useEffect deps so
  // React compares by value even when the underlying arrays are new references.
  // The split-back arrays are used inside filter callbacks.
  const selectedProviderKey = Array.isArray(filterValues.provider)
    ? (filterValues.provider as string[])
        .map((v) => v.toLowerCase())
        .filter((v) => v !== '')
        .sort()
        .join(',')
    : '';
  const selectedModuleKey = Array.isArray(filterValues.module)
    ? (filterValues.module as string[])
        .map((v) => v.toLowerCase())
        .filter((v) => v !== '')
        .sort()
        .join(',')
    : '';
  // ── Benchmark options — filtered by selected provider + module ─────────────
  // All benchmark data is in memory from the single API call; this is instant.
  const visibleBenchmarks = React.useMemo(() => {
    const providers = selectedProviderKey ? selectedProviderKey.split(',') : [];
    const modules = selectedModuleKey ? selectedModuleKey.split(',') : [];
    return benchmarkOptions.filter((opt) => {
      if (providers.length > 0 && opt.provider) {
        if (!providers.includes(opt.provider.toLowerCase())) return false;
      }
      if (modules.length > 0 && opt.module) {
        if (!modules.includes(opt.module.toLowerCase())) return false;
      }
      return true;
    });
  }, [benchmarkOptions, selectedProviderKey, selectedModuleKey]);

  // ── Integration options — filtered by selected provider + module ───────────
  const visibleIntegrations = React.useMemo(() => {
    const providers = selectedProviderKey ? selectedProviderKey.split(',') : [];
    const modules = selectedModuleKey ? selectedModuleKey.split(',') : [];
    return integrationOptions.filter((opt) => {
      if (providers.length > 0 && opt.provider) {
        if (!providers.includes(opt.provider.toLowerCase())) return false;
      }
      if (modules.length > 0 && opt.module) {
        if (!modules.includes(opt.module.toLowerCase())) return false;
      }
      return true;
    });
  }, [integrationOptions, selectedProviderKey, selectedModuleKey]);

  // ── Region options — filtered by selected provider ONLY ───────────────────────
  // Module must not affect region visibility (per product spec).
  const visibleRegions = React.useMemo(() => {
    const providers = selectedProviderKey ? selectedProviderKey.split(',') : [];
    return providers.length === 0
      ? regionOptions
      : regionOptions.filter(
          (opt) =>
            opt.provider && providers.includes(opt.provider.toLowerCase()),
        );
  }, [regionOptions, selectedProviderKey]);

  // ── Clear stale benchmark / region / integration selections when provider or module changes
  // Prevents invalid values from being sent to the backend and informs the user.
  useEffect(() => {
    // Only run AFTER URL hydration has completed. During hydration the atoms
    // may still hold the PREVIOUS page's selection (Jotai atoms persist across
    // route changes) while the option lists already reflect the NEW deep-link
    // (e.g. the provisional integration option). Running now would compare the
    // old selection against the new options and wrongly wipe the just-applied
    // integration filter on repeat navigations.
    if (!isQuerystringFiltersInitialized) return;

    // Guard: wait until at least one options list has loaded from the API.
    if (
      benchmarkOptions.length === 0 &&
      regionOptions.length === 0 &&
      integrationOptions.length === 0
    )
      return;

    // Recompute valid sets inline so this effect's dep array stays minimal.
    const providers = selectedProviderKey ? selectedProviderKey.split(',') : [];
    const modules = selectedModuleKey ? selectedModuleKey.split(',') : [];

    const validBenchmarks = benchmarkOptions.filter((opt) => {
      if (providers.length > 0 && opt.provider) {
        if (!providers.includes(opt.provider.toLowerCase())) return false;
      }
      if (modules.length > 0 && opt.module) {
        if (!modules.includes(opt.module.toLowerCase())) return false;
      }
      return true;
    });
    const validIntegrations = integrationOptions.filter((opt) => {
      if (providers.length > 0 && opt.provider) {
        if (!providers.includes(opt.provider.toLowerCase())) return false;
      }
      if (modules.length > 0 && opt.module) {
        if (!modules.includes(opt.module.toLowerCase())) return false;
      }
      return true;
    });
    const validRegions =
      providers.length === 0
        ? regionOptions
        : regionOptions.filter(
            (opt) =>
              opt.provider && providers.includes(opt.provider.toLowerCase()),
          );

    const validBenchmarkSet = new Set(validBenchmarks.map((o) => o.value));
    const validIntegrationSet = new Set(validIntegrations.map((o) => o.value));
    const validRegionSet = new Set(validRegions.map((o) => o.value));

    // Read via ref — current values without triggering on every filter change.
    const currentBenchmarks =
      (filterValuesRef.current.benchmark as string[] | undefined) ?? [];
    const currentIntegrations =
      (filterValuesRef.current.integration_ids as string[] | undefined) ?? [];
    const currentRegions =
      (filterValuesRef.current.region as string[] | undefined) ?? [];

    // Only treat a selection as "stale" when that filter's OWN option list has
    // actually loaded. The option lists arrive via separate setState calls, so
    // there is an intermediate render where e.g. region/benchmark options are
    // populated but integration options are not. Without these per-list guards,
    // a valid deep-linked selection would be wrongly wiped during that window.
    const benchmarkCleared =
      benchmarkOptions.length > 0 &&
      currentBenchmarks.some((v) => v !== '' && !validBenchmarkSet.has(v));
    const integrationCleared =
      integrationOptions.length > 0 &&
      currentIntegrations.some((v) => v !== '' && !validIntegrationSet.has(v));
    const regionCleared =
      regionOptions.length > 0 &&
      currentRegions.some((v) => v !== '' && !validRegionSet.has(v));

    if (benchmarkCleared) {
      setFilterValues((prev) => ({ ...prev, benchmark: [] }));
    }
    if (integrationCleared) {
      setFilterValues((prev) => ({ ...prev, integration_ids: [] }));
    }
    if (regionCleared) {
      setFilterValues((prev) => ({ ...prev, region: [] }));
    }

    if (benchmarkCleared || integrationCleared || regionCleared) {
      const cleared: string[] = [];
      if (benchmarkCleared) cleared.push('Benchmark');
      if (integrationCleared) cleared.push('Integration');
      if (regionCleared) cleared.push('Region');
      showInfoToast(
        `${cleared.join(', ')} filter${cleared.length > 1 ? 's were' : ' was'} cleared — the previous selection is not available for the current provider${selectedModuleKey ? '/module' : ''} selection.`,
      );
    }
  }, [
    isQuerystringFiltersInitialized,
    selectedProviderKey,
    selectedModuleKey,
    benchmarkOptions,
    integrationOptions,
    regionOptions,
    setFilterValues,
  ]);

  // ── Build the FilterConfig (memoised to avoid recreation on every render) ──
  // Uses applyInitialFilterValues (reference pattern) so that values stored in
  // Jotai atoms — including date-range sub-filters inside FilterGroupConfig —
  // are correctly applied to the rendered controls on every re-render.
  const config = React.useMemo(() => {
    const newConfig = new FilterConfig({
      searchFilterId: 'search',
      rightPanelFilters: [
        'scan_status',
        'severity',
        ...(providerOptions.length > 1 ? ['provider'] : []),
        ...(moduleOptions.length > 1 ? ['module'] : []),
        'benchmark',
        'assignment_filters',
      ],
      leftPanelFilters: [
        'integration_ids',
        'region',
        'dateRangeFilters',
        'control_id',
      ],
      filters: [
        // ── Search ────────────────────────────────────────────────────────
        new SearchInputFilterConfig({
          id: 'search',
          label: 'Search',
          placeholder: 'Search and Filter',
          tooltip:
            'Search across control description, control ID, description, and integration label',
          defaultSelectedValues: '',
          maxLength: 250,
          debounceTime: 1000,
          enableDebounce: true,
        }),

        new InputFilterConfig({
          id: 'control_id',
          label: 'Control ID',
          tooltip: 'Filter by control ID.',
          iconClass: 'fas fa-id-card',
          inputProps: {
            type: 'number',
            placeholder: 'Control ID',
            maxLength: 50,
          },
          debounceTime: 1000,
          enableDebounce: true,
        }),

        // ── Date Range Filters Group ───────────────────────────────────────
        // currentSelectedValues are injected by applyInitialFilterValues below,
        // which correctly traverses FilterGroupConfig sub-filters.
        new FilterGroupConfig({
          id: 'dateRangeFilters',
          label: 'Filter by Date',
          iconClass: 'fas fa-calendar-alt',
          tooltip:
            'Filter by date and timestamps. The From and To dates are inclusive.',
          filters: [
            new DateRangeFilterConfig({
              id: 'first_seen_at',
              label: 'First Detected',
              labelWidth: '100px',
            }),
            new DateRangeFilterConfig({
              id: 'last_seen_at',
              label: 'Last Detected',
              labelWidth: '100px',
            }),
            new DateRangeFilterConfig({
              id: 'last_fixed_at',
              label: 'Last Fixed',
              labelWidth: '100px',
            }),
            new DateRangeFilterConfig({
              id: 'created_at',
              label: 'Created Date',
              labelWidth: '100px',
            }),
            new DateRangeFilterConfig({
              id: 'ignored_date',
              label: 'Ignored',
              labelWidth: '100px',
            }),
          ],
        }),

        // ── Severity — static, Badges (single-select) ─────────────────────
        new MultiSelectFilterConfig({
          id: 'severity',
          label: 'Severity',
          tooltip: 'Filter by severity level',
          iconClass: 'fas fa-exclamation-triangle',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            { label: 'Critical', value: '4' },
            { label: 'High', value: '3' },
            { label: 'Medium', value: '2' },
            { label: 'Low', value: '1' },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),

        // ── Scan Status — static, Badges (single-select) ──────────────────
        new MultiSelectFilterConfig({
          id: 'scan_status',
          label: 'Status',
          tooltip: 'Filter by status',
          iconClass: 'fas fa-tasks',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            { label: 'Active', value: 'Active' },
            { label: 'Fixed', value: 'Fixed' },
            { label: 'Ignored', value: 'Ignored' },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),

        // ── Assignment filters ─────────────────────────────────────────────
        new MultiSelectFilterConfig({
          id: 'assignment_filters',
          label: 'Predefined Filters',
          tooltip: 'Filter by predefined filters',
          iconClass: 'fas fa-user-check',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            {
              label: 'Assigned',
              value: 'assigned=true',
              deselectOthers: true,
            },
            {
              label: 'Assigned to Me',
              value: 'self_assigned=true',
              deselectOthers: true,
            },
            {
              label: 'Watching (by me)',
              value: 'self_watching=true',
              deselectOthers: true,
            },
            {
              label: 'Unread Notes',
              value: 'unread_notes=true',
              deselectOthers: true,
            },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),

        new IconMultiSelectFilterConfig({
          id: 'benchmark',
          label: 'Benchmark',
          tooltip: 'Filter by compliance benchmark',
          iconClass: 'fas fa-book',
          options: visibleBenchmarks,
          defaultSelectedValues: [],
        }),

        // ── Integration — always visible; options filtered by provider + module ────
        new IconMultiSelectFilterConfig({
          id: 'integration_ids',
          label: 'Integrations',
          tooltip: 'Filter by cloud integration',
          iconClass: 'fas fa-plug',
          options: visibleIntegrations,
          defaultSelectedValues: [],
        }),

        // ── Region — always visible; options filtered by provider only ────────────
        new IconMultiSelectFilterConfig({
          id: 'region',
          label: 'Region',
          tooltip: 'Filter by cloud region',
          iconClass: 'fas fa-globe',
          options: visibleRegions,
          defaultSelectedValues: [],
        }),

        // ── Provider — dynamic, Badges (single-select), conditional ───────
        ...(providerOptions.length > 1
          ? [
              new MultiSelectFilterConfig({
                id: 'provider',
                label: 'Cloud Provider',
                placeholder: 'Select provider',
                tooltip: 'Filter by cloud provider',
                iconClass: 'fas fa-cloud',
                defaultSelectedValues: [''],
                options: [
                  { label: 'All', value: '', deselectOthers: true },
                  ...providerOptions.map((o) => ({
                    label: o.value,
                    value: o.value,
                  })),
                ],
                renderStyle: MultiSelectRenderStyle.Badges,
              }),
            ]
          : []),

        // ── Module — dynamic, Badges (single-select), conditional ──────────
        ...(moduleOptions.length > 1
          ? [
              new MultiSelectFilterConfig({
                id: 'module',
                label: 'Module',
                placeholder: 'Select module',
                tooltip: 'Filter by security module',
                iconClass: 'fas fa-cube',
                defaultSelectedValues: [''],
                options: [
                  { label: 'All', value: '', deselectOthers: true },
                  ...moduleOptions.map((o) => ({
                    label: o.value,
                    value: o.value,
                  })),
                ],
                renderStyle: MultiSelectRenderStyle.Badges,
              }),
            ]
          : []),
      ],
    });

    // Apply stored filter values to all filters, including DateRangeFilterConfig
    // sub-filters nested inside FilterGroupConfig. This is the only way to
    // correctly hydrate date pickers from atom state.
    newConfig.applyInitialFilterValues(filterValues);
    return newConfig;
  }, [
    filterValues,
    providerOptions,
    moduleOptions,
    visibleBenchmarks,
    visibleIntegrations,
    visibleRegions,
  ]);

  return config;
};
